import { Ref, ref, watch } from "vue"
import {
  AppNameEnum,
  UserTypeEnum,
  GetMeResponse,
  BaseUser,
  VitableStandardAuthenticationResponse,
  VitablePasswordlessAuthenticationResponse,
  UserApiUpdatePasswordRequest
} from "@vitable/asclepius-api-client-ts"
import useAsclepius from "@/composables/api/asclepius"
import { version } from "../../package.json"
import { useUserStore } from "@/store/user-store"
import { useRouter } from "vue-router"

const APP_NAME: AppNameEnum = AppNameEnum.UserDashboard
const APP_VERSION: string = version

export type StandardLoginParams = {
  phone: string
  userType?: UserTypeEnum
  password: string
  postLoginRedirectPath?: string
  postLoginRedirectPageName?: string
}

export type PasswordlessLoginParams = {
  phone: string
  userType?: UserTypeEnum
  token?: string | null
  postLoginRedirectPath?: string
  postLoginRedirectPageName?: string
  doNotAutoNavigateHome?: boolean
}

export function useLoginStandard() {
  const { getAuthenticationApi, call, callResult } = useAsclepius()

  const userStore = useUserStore()
  const router = useRouter()

  const postLoginRedirectPathRef = ref<string>()
  const postLoginRedirectPageNameRef = ref<string>()

  async function loginStandard(params: StandardLoginParams): Promise<void> {
    if (!params.phone) {
      throw Error("You need a phone to login!")
    } else if (!params.password) {
      throw Error("You need a password to login!")
    }

    postLoginRedirectPathRef.value = params.postLoginRedirectPath
    postLoginRedirectPageNameRef.value = params.postLoginRedirectPageName

    await call(async () =>
      getAuthenticationApi().authenticateWithStandard({
        vitableStandardAuthenticationRequest: {
          email_or_phone: params.phone,
          user_type: params.userType ?? UserTypeEnum.Member,
          password: params.password,
          app_name: APP_NAME,
          app_version: APP_VERSION
        }
      })
    )
  }

  watch(callResult, async (newResult) => {
    if (newResult.isSuccess()) {
      const data: VitableStandardAuthenticationResponse = newResult.getData()

      if (data.is_pending_2fa_token) {
        throw Error("2FA is not supported for members at this time!")
      }

      await userStore.login(data.user!, data.token!)
      if (postLoginRedirectPathRef.value) {
        window.location.href = postLoginRedirectPathRef.value
      } else if (postLoginRedirectPageNameRef.value) {
        await router.push({ name: postLoginRedirectPageNameRef.value })
      } else {
        await router.push({ name: "home" })
      }
    }
  })

  return {
    loginStandard,
    loginStandardResult: callResult
  }
}

export function useLoginPasswordless() {
  const { getAuthenticationApi, call, callResult } = useAsclepius()

  const isLoginLoading = ref(false)

  const userStore = useUserStore()
  const router = useRouter()

  const postLoginRedirectPathRef = ref<string>()
  const postLoginRedirectPageNameRef = ref<string>()
  const doNotAutoNavigateHome = ref<boolean>()

  async function loginPasswordless(params: PasswordlessLoginParams): Promise<void> {
    if (!params.phone) {
      throw Error("You need a phone to login!")
    }

    isLoginLoading.value = true

    postLoginRedirectPathRef.value = params.postLoginRedirectPath
    postLoginRedirectPageNameRef.value = params.postLoginRedirectPageName
    doNotAutoNavigateHome.value = params.doNotAutoNavigateHome

    await call(async () =>
      getAuthenticationApi().authenticateWithPasswordless({
        vitablePasswordlessAuthenticationRequest: {
          email_or_phone: params.phone,
          user_type: params.userType ?? UserTypeEnum.Member,
          token: params.token ?? null,
          app_name: APP_NAME,
          app_version: APP_VERSION
        }
      })
    )
  }

  watch(callResult, async (newResult) => {
    if (newResult.isSuccess()) {
      const data: VitablePasswordlessAuthenticationResponse = newResult.getData()
      if (!data.is_pending_passwordless_token) {
        await userStore.login(data.user!, data.token!)
        if (postLoginRedirectPathRef.value) {
          window.location.href = postLoginRedirectPathRef.value
        } else if (postLoginRedirectPageNameRef.value) {
          await router.push({ name: postLoginRedirectPageNameRef.value, query: router.currentRoute.value.query })
        } else if (!doNotAutoNavigateHome.value) {
          await router.push({ name: "home", query: router.currentRoute.value.query })
        }
      }
      isLoginLoading.value = false
    } else if (newResult.isFailure()) {
      isLoginLoading.value = false
    }
  })

  return {
    loginPasswordless,
    loginPasswordlessResult: callResult,
    isLoginLoading
  }
}

export function useUpdatePassword() {
  const { getUserApi, call, callResult } = useAsclepius()

  async function updatePassword(newPassword: string): Promise<void> {
    const request: UserApiUpdatePasswordRequest = {
      updatePasswordRequest: {
        new_password: newPassword
      }
    }

    await call(async () => getUserApi().updatePassword(request))
  }

  return {
    updatePassword,
    updatePasswordResult: callResult
  }
}

export function useGetMe() {
  const { getAuthenticationApi, call, callResult } = useAsclepius()
  const me: Ref<Nullable<BaseUser>> = ref(null)

  async function getMe(): Promise<void> {
    await call(async () => getAuthenticationApi().getMe())
  }

  watch(callResult, (newResult) => {
    if (newResult.isSuccess()) {
      const data: GetMeResponse = newResult.getData()
      if (data.me) {
        me.value = data.me
      }
    }
  })

  return {
    me,
    getMe,
    getMeResult: callResult
  }
}
